class MonitoringMessageBuffer {
  constructor() {
    this.maxMessages = Number(process.env.REACT_APP_MONITORING_BUFFER_MAX_MESSAGES || 250000);
    this.maxSeenIds = Math.max(this.maxMessages * 2, 20000);
    this.retentionMs = Number(process.env.REACT_APP_MONITORING_BUFFER_RETENTION_MS || 60 * 60 * 1000);
    this.messages = [];
    this.seenIds = new Set();
    this.seenIdOrder = [];
    this.nextSequence = 1;
    this.latestSequence = 0;
    this.totalMessagesReceived = 0;
    this.lastMessageReceivedAt = null;
    this.lastRetentionTrimAt = 0;
    this.retentionTrimIntervalMs = 5000;
  }

  addMessages(incomingMessages) {
    const acceptedMessages = [];

    (Array.isArray(incomingMessages) ? incomingMessages : []).forEach(message => {
      if (!message) {
        return;
      }

      const messageId = this.getMessageId(message);
      if (messageId) {
        if (this.seenIds.has(messageId)) {
          return;
        }

        this.seenIds.add(messageId);
        this.seenIdOrder.push(messageId);
      }

      const bufferedMessage = { ...message };
      Object.defineProperty(bufferedMessage, '_monitoringSequence', {
        value: this.nextSequence++,
        enumerable: false
      });
      Object.defineProperty(bufferedMessage, '_monitoringTimestamp', {
        value: this.parseMessageTimestamp(bufferedMessage),
        enumerable: false
      });
      acceptedMessages.push(bufferedMessage);
    });

    if (acceptedMessages.length > 0) {
      this.messages.push(...acceptedMessages);
      this.latestSequence = acceptedMessages[acceptedMessages.length - 1]._monitoringSequence;
      this.totalMessagesReceived += acceptedMessages.length;
      this.lastMessageReceivedAt = Date.now();
      this.enforceMaxMessages();
      if (this.lastMessageReceivedAt - this.lastRetentionTrimAt >= this.retentionTrimIntervalMs) {
        this.trimMessages(this.lastMessageReceivedAt);
        this.lastRetentionTrimAt = this.lastMessageReceivedAt;
      }
      this.trimSeenIds();
    }

    return this.getStats(acceptedMessages.length);
  }

  getRecentMessages() {
    return this.messages;
  }

  getMessageCount() {
    return this.messages.length;
  }

  getMessagesAfter(sequence = 0) {
    if (this.messages.length === 0) {
      return {
        messages: [],
        nextSequence: this.latestSequence
      };
    }

    const messages = [];

    for (let i = this.messages.length - 1; i >= 0; i -= 1) {
      const message = this.messages[i];
      const messageSequence = message._monitoringSequence || 0;

      if (messageSequence <= sequence) {
        break;
      }

      messages.push(message);
    }

    return {
      messages: messages.reverse(),
      nextSequence: this.latestSequence
    };
  }

  getMessagesInInterval(intervalStart, intervalEnd) {
    return this.messages.filter(message => {
      const timestamp = this.parseMessageTimestamp(message);
      return timestamp && timestamp >= intervalStart && timestamp < intervalEnd;
    });
  }

  getStats(acceptedCount = 0) {
    return {
      acceptedCount,
      totalMessagesReceived: this.totalMessagesReceived,
      recentMessageCount: this.messages.length,
      lastMessageReceivedAt: this.lastMessageReceivedAt
    };
  }

  clear() {
    this.messages = [];
    this.seenIds.clear();
    this.seenIdOrder = [];
    this.nextSequence = 1;
    this.latestSequence = 0;
    this.totalMessagesReceived = 0;
    this.lastMessageReceivedAt = null;
    this.lastRetentionTrimAt = 0;
  }

  getMessageId(message) {
    return message.leader_id || message.id || message.messageId || null;
  }

  parseMessageTimestamp(message) {
    if (message && typeof message._monitoringTimestamp === 'number') {
      return message._monitoringTimestamp;
    }

    const timestampValue = message.leader_timestamp || message.timestamp || message.created_at || null;
    if (!timestampValue) {
      return null;
    }

    const parsed = typeof timestampValue === 'number'
      ? timestampValue
      : Date.parse(timestampValue);

    return Number.isNaN(parsed) ? null : parsed;
  }

  trimSeenIds() {
    while (this.seenIdOrder.length > this.maxSeenIds) {
      const oldestId = this.seenIdOrder.shift();
      this.seenIds.delete(oldestId);
    }
  }

  trimMessages(currentTime = Date.now()) {
    const cutoffTime = currentTime - this.retentionMs;

    this.messages = this.messages.filter(message => {
      const timestamp = this.parseMessageTimestamp(message) || currentTime;
      return timestamp >= cutoffTime;
    });

    this.enforceMaxMessages();
  }

  enforceMaxMessages() {
    if (this.messages.length > this.maxMessages) {
      this.messages = this.messages.slice(this.messages.length - this.maxMessages);
    }
  }
}

const monitoringMessageBuffer = new MonitoringMessageBuffer();

export default monitoringMessageBuffer;
